import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { mirrorLimits, schedulerLimits } from "configs/limits";
import { Table } from "components/table/Table";
import Pagination from "components/Pagination";
import { openModal } from "store/modals/slice";
import { ModalTypes } from "configs/types";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import SwitchBtn from "components/buttons/SwitchBtn";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import {
  getSchedulerListPayload,
  handleSelectAll,
  handleSelect,
  handlePaginate,
  handleClearFields,
  SchedulerItem,
  IScheduler,
  deleteSchedulerItem
} from "store/scheduler/slice";
import SchedulerFilter from "./components/SchedulerFilter";
import { SchedulerHeader } from "./components/SchedulerHeader";
import { ReactComponent as Clock } from '../../assets/icons/clock.svg';
import cn from 'clsx';
import styles from './Scheduler.module.scss';

type Props = {};

const SchedulerList: React.FC<Props> = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const permissions = usePermissionHelper();

  const isListFetched = useAppSelector((state) => state.scheduler.isListFetched);
  const schedulerList = useAppSelector((state) => state.scheduler.list);
  const totalItems = useAppSelector((state) => state.scheduler.totalItems);
  const totalPages = useAppSelector((state) => state.scheduler.totalPages);
  const activePage = useAppSelector((state) => state.scheduler.activePage);
  const selectedItems = useAppSelector((state) => state.scheduler.selectedItems);
  const processingItemId = useAppSelector((state) => state.scheduler.processingItemId);

  const [limit, setLimit] = useState<number>(schedulerLimits[1]);

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'scheduler',
    isListFetched: isListFetched
  });

  const handleSelectSchedule = (id: SchedulerItem['id']) => {
    dispatch(handleSelect(id));
  };

  const handleSelectAllSchedulers = () => {
    dispatch(handleSelectAll());
  };
  
  const handlePreviousClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage < totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleDeleteItem = async (id: number) => {
    if (!permissions?.delete) {
      return;
    }

    const result = await dispatch(deleteSchedulerItem(id));

    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };


  useEffect(() => {
    dispatch(getSchedulerListPayload({
      limit,
      offset: activePage - 1,
    }))

  }, [activePage, limit, isListFetched]);

  const schedule = (schedule: IScheduler) => {
    if (!schedule.type) {
      return (<span>-</span>);
    }

    const days = [
      t("sun"),
      t("mon"),
      t("tue"),
      t("wed"),
      t("thu"),
      t("fri"),
      t("sat")
    ];

    const typeSchedule = () => {
      switch (schedule.type) {
        case "specific":
          return schedule.specific.join(", ");
        case "interval":
          return t("every");
        default:
          return '-';
      };
    };

    const daysToRender = schedule.days.map((_, idx) => days[idx]);

    return (
      <div className={cn(styles.scheduler)}>
        <span className={cn(
          styles.schedulerIcon,
          schedule.type === "interval" && styles.schedulerIconGreen,
          schedule.type === "specific" && styles.schedulerIconBlue
        )}>
          <Clock />
        </span>
        <span>
          {typeSchedule()}
        </span>
        <span>&bull;</span>
        <span>
          {daysToRender.join(", ")}
        </span>
      </div>
    );
  };

  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.scheduler}`);
    dispatch(openModal({ activeModal: ModalTypes.scheduler }));
  };

  const handleOpenEditModal = (id: number) => {
    dispatch(handleClearFields());
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.scheduler}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.scheduler }));
  };

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };


  return (
    <div className="page-list-wrapper page-list--with-filter">
      <SchedulerFilter
        filterByTitle={''}
        filterByIsActive={[]}
        filterByType={[]}
        onFilterSearchByTitle={() => {}}
        onFilterByIsActive={() => {}}
        onFilterByType={() => {}}
        onClearFilter={() => {}}
        handleApply={() => { }}
        disabled
      />
      <div className="page-list--with-filter-content">
        <SchedulerHeader
          options={[]}
          createModal={handleOpenCreateModal}
          isDisabled
          isCreateAllowed={permissions?.create}
          refresh={refresh}
          isRefreshRequested={isRefreshRequested}
        />
        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={8} rowsCount={4} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && schedulerList.length === 0 && (
          <EmptyState message={t("noSchedulerFound")} />
        )}

        {isListFetched && schedulerList.length > 0 && (
          <div className="game-table-wrap">
            <Table
              data={schedulerList}
              idField="id"
              onSort={(v) => {
                const k = v as unknown;
                // handleOrderClick(k as keyof typeof order);
              }}
              // order={order}
              onAllSelect={handleSelectAllSchedulers}
              onSelect={handleSelectSchedule}
              onDelete={handleDeleteItem}
              selectedItems={selectedItems}
              // customOptions={(itm) => (
              //   <ListOptions
              //     id={itm.id}
              //     onEdit={(r) => handleOpenEditModal(r as string)}
              //     onToggle={() =>
              //       handleToogleItem(itm, itm.status !== "removed")
              //     }
              //     isDisable={itm.status === "removed"}
              //     readOnly={!permissions?.update}
              //   />
              // )}
              // customColumns={(itm) => ({
              //   isDefault:
              //     itm.status === "removed" ? (
              //       <div style={{ textAlign: "center" }}>-</div>
              //     ) : (
              //       <SwitchBtn
              //         handleClick={() => handleToggleIsDefault(itm.id)}
              //           isActive={itm.isDefault === 1}
              //           disabled={!permissions?.update}
              //       />
              //     ),
              // })}
              columns={{
                sortingColumns: {
                  id: {
                    value: t("id"),
                    hasSorting: false,
                  },
                },
              }}
              customColumns={(row) => ({
                schedule: schedule(row.schedule)
              })}
              processingId={processingItemId}
              hasDelete={permissions?.delete}
              hasEdit={permissions?.update}
              hasView={permissions?.read && !permissions?.update}
              hasOptions={permissions?.read || permissions?.update}
              {...actionProps}
            />
          </div>
        )}

        {isListFetched && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
            limitsArray={schedulerLimits}
            activePage={activePage}
            limit={limit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePreviousClick}
          />
        )}
      </div>
    </div>
  );
};

export default SchedulerList;
